<!--
 * @Descripttion: 星盘页「开通功能」弹窗（公共组件）
 *   列出后端支持的 11 项可购买权限（会员 / 虚星 / 行星力量 / 催贵公式 / 本命盘 /
 *   和谐相位搜索 / 星历表下载 / 天运 / 座山指针 / 点报警 / 面报警），点选后展示价格与微信支付二维码，
 *   扫码支付成功由后端回调自动开通，前端轮询查单成功后写回登录态并通知宿主刷新权限。
 *   支付链路与会员弹窗（VipPurchaseDialog）完全一致：/yljx/public/price + pay/h5pay + lunxun。
 *
 *   其中价格为 0 的项（点报警 / 面报警等）不走支付：点一下即由 `/yljx/public/permission-claim`
 *   直接开通一年，卡片随即置灰锁定，再次点击不改写也不延长已开通的年份（服务端同样幂等拦住）。
 * @params:
 *   visible  [Boolean] 显示控制（配合 .sync 使用）
 *   userId   [String|Number] 当前登录用户 id（users.id），缺失时提示先登录
-->
<template>
  <!-- append-to-body 必须保留：星盘页存在 `#xxx > * { position: relative }` 通配规则，
       不挂到 body 时弹窗会掉进文档流并失去遮罩关闭 -->
  <el-dialog
    title="开通功能"
    :visible.sync="dialogVisible"
    width="80%"
    append-to-body
    :close-on-click-modal="false"
    :before-close="handleClose">
    <p class="feature-purchase-subtitle">选择需要开通的功能：付费项扫码支付后自动开通，0 元项点击后直接开通一年</p>

    <div class="feature-purchase-body">
      <div class="feature-purchase-list">
        <div
          v-for="item in items"
          :key="item.key"
          class="feature-card"
          :class="{
            'is-active': selectedKey === item.key,
            'is-free': isFree(item),
            'is-disabled': !item.purchasable || isCardLocked(item)
          }"
          @click="selectItem(item)">
          <div class="feature-card-head">
            <span class="feature-card-name">{{ item.name }}</span>
            <span class="feature-badge" :class="'is-' + item.status.status">{{ item.status.label }}</span>
          </div>
          <div class="feature-card-desc">{{ item.desc }}</div>
          <div class="feature-card-foot">
            <span class="feature-card-price">{{ displayPrice(item) }}</span>
            <span v-if="item.status.expiryDate" class="feature-card-expiry">到期 {{ item.status.expiryDate }}</span>
          </div>
          <!-- 0 元项：点一下即开通一年，开通后本卡片置灰并锁定，不再允许重复点击 -->
          <div v-if="isFree(item)" class="feature-card-hint" :class="{ 'is-locked': isCardLocked(item) }">
            {{ freeHint(item) }}
          </div>
        </div>
      </div>

      <div class="feature-purchase-pay">
        <div class="feature-pay-title">{{ selectedItem ? selectedItem.name : '请选择功能' }}</div>
        <div class="feature-pay-amount">{{ selectedItem ? displayPrice(selectedItem) : '—' }}</div>

        <div v-if="qrUrl" class="feature-qr-wrap">
          <img class="feature-qr" :src="qrUrl" alt="微信支付二维码">
          <p class="feature-qr-tip">{{ scanTip }}</p>
        </div>
        <div v-else class="feature-qr-placeholder">
          <span>{{ payHint }}</span>
        </div>

        <div v-if="orderId" class="feature-order-no">订单号：{{ orderId }}</div>
        <div class="feature-pay-status" :class="'is-' + payState">
          <template v-if="payState === 'claiming'">正在开通…</template>
          <template v-else-if="payState === 'pending'">等待支付…</template>
          <template v-else-if="payState === 'success'">已开通{{ successExpiry ? '，到期日 ' + successExpiry : '' }}</template>
          <template v-else-if="payState === 'failed'">{{ payMessage || '支付未完成，请重新下单' }}</template>
          <template v-else>点击左侧功能卡片开始开通</template>
        </div>
        <el-button
          v-if="payState === 'failed' && selectedItem && !isFree(selectedItem)"
          size="mini"
          type="warning"
          plain
          @click="startPurchase(selectedItem)">重新下单</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import apiReq from '@t/api'
import purchaseCatalog from '@t/utils/permissionPurchaseCatalog'
import { ensureWechatBound, invokeWechatPay, ensureScanPayBound, SCAN_PAY_TEXT } from '@t/utils/wechatPay'
// 登录态统一写入口 + 刷新入口：支付成功后写回最新 user 并广播，页面无需整页刷新即可生效
const userContext = require('../../../services/userContext')
const refreshUser = require('@t/utils/refreshTianxingUser')

/** 星盘页订单来源标记，后端写入 permission_orders.source 便于对账区分渠道 */
const PURCHASE_SOURCE = 'astrolabe_onlinechart'

/** 轮询上限：1 秒一次，最多 10 分钟，避免用户关掉支付页后前端一直空转 */
const MAX_POLL_COUNT = 600

export default {
  name: 'FeaturePurchaseDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [String, Number],
      default: ''
    },
    // 打开弹窗时希望「定位并高亮」的权益 key（如从「相位搜索」无权限引导过来时传
    // 'harmonious_phase_search'）。只做选中高亮，不会自动下单支付。
    defaultKey: {
      type: String,
      default: ''
    },
    // 订单来源标记（写入后端 permission_orders.source，便于对账区分渠道）。
    // 默认值保持星盘页原口径不变；其它入口（如首页「星盘权限开通」）传自己的渠道名即可。
    purchaseSource: {
      type: String,
      default: PURCHASE_SOURCE
    }
  },
  data () {
    return {
      dialogVisible: false,
      items: [],
      selectedKey: '',
      qrUrl: '',
      orderId: '',
      payState: 'idle',
      payMessage: '',
      payHint: '选择功能后生成微信支付二维码',
      successExpiry: '',
      pollTimer: null,
      pollCount: 0
    }
  },
  computed: {
    selectedItem () {
      return this.items.find(item => item.key === this.selectedKey) || null
    },
    /** 付款码下方统一扫码提示（与其它扫码入口同一口径） */
    scanTip () {
      return SCAN_PAY_TEXT.scanTip
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val) {
        this.refreshItems()
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
      if (!val) {
        this.stopPolling()
      }
    }
  },
  created () {
    this.dialogVisible = this.visible
    this.refreshItems()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    formatPrice (price) {
      return purchaseCatalog.formatPrice(price)
    },
    /** 是否为「0 元项」：价格实时判定，后台把价格改回非 0 即自动退回扫码支付流程 */
    isFree (item) {
      return purchaseCatalog.isFreeItem(item)
    },
    /**
     * 0 元项是否已被锁定：已开通且未到期 → 卡片置灰、点击不再放行。
     * 对应需求「首次点击后按钮不可再点，且不得修改或延长已开通的年份」。
     * 服务端 {@code /permission-claim} 有同样的幂等判断，这里只是不给入口。
     */
    isCardLocked (item) {
      return this.isFree(item) && !purchaseCatalog.canClaimFreeItem(item)
    },
    /** 0 元项没有支付动作，价格位直接显示「免费」而不是 ￥0.00 */
    displayPrice (item) {
      return this.isFree(item) ? '免费' : purchaseCatalog.formatPrice(item.price)
    },
    freeHint (item) {
      return this.isCardLocked(item) ? '已开通，无需重复开通' : '点击即可开通一年（0 元，无需支付）'
    },
    readCurrentUser () {
      try {
        const raw = localStorage.getItem('user')
        return raw ? JSON.parse(raw) : null
      } catch (error) {
        return null
      }
    },
    /**
     * 拉目录 + 价格 + 解析已开通状态。
     * 优先用后端目录接口 `permissionItems`（key/中文名/说明/价格的唯一权威来源）；
     * 接口异常时回退到「本地目录 + /price」，价格缺失的项按不可购买置灰。
     */
    async refreshItems () {
      const user = this.readCurrentUser() || {}
      let merged = []

      try {
        const serverItems = await apiReq.permissionItems()
        merged = purchaseCatalog.mergeCatalogWithServerItems(serverItems)
      } catch (error) {
        console.error('[FeaturePurchaseDialog] 获取权限目录失败，回退本地目录:', error)
      }

      if (!merged.length) {
        let priceList = []
        try {
          priceList = await apiReq.price()
        } catch (error) {
          console.error('[FeaturePurchaseDialog] 获取权限价格失败:', error)
        }
        merged = purchaseCatalog.mergeCatalogWithPrices(priceList)
      }

      this.items = merged.map(item => ({
        ...item,
        status: purchaseCatalog.resolveEntitlementStatus(item, user)
      }))

      this.applyDefaultSelection()
    },
    /**
     * 按 defaultKey 定位并高亮某一项（供「无权限 → 引导开通」使用）。
     * 只设置 selectedKey 做视觉定位，**不调用 selectItem**，避免一进弹窗就自动下单出二维码。
     */
    applyDefaultSelection () {
      const key = String(this.defaultKey || '').trim()
      if (!key) {
        return
      }
      if (!this.items.some(item => item.key === key)) {
        return
      }
      this.selectedKey = key
    },
    selectItem (item) {
      if (!item) {
        return
      }
      // 0 元项：点一下即开通一年（不生成微信订单），已开通则锁定不再放行
      if (this.isFree(item)) {
        this.claimFreeItem(item)
        return
      }
      if (!item.purchasable) {
        this.$message && this.$message.warning('该项暂未开放购买，请联系客服')
        return
      }
      this.selectedKey = item.key
      this.startPurchase(item)
    },
    /**
     * 0 元项自助开通（`POST /yljx/public/permission-claim`）。
     *
     * 与扫码支付的区别：不产生微信订单、不轮询，一次请求即完成。
     * 后端返回最新 user（code=1111），这里写回登录态并刷新卡片状态；
     * 「已开通未到期」的重复点击被前后端同时拦下，返回的到期时间与首次开通完全一致，
     * 既不改写也不顺延。
     */
    async claimFreeItem (item) {
      if (!item || !this.isFree(item)) {
        return
      }
      this.selectedKey = item.key

      if (this.isCardLocked(item)) {
        this.$message && this.$message.info('该功能已开通，无需重复开通')
        return
      }

      const user = this.readCurrentUser()
      if (!user || user.id === undefined || user.id === null) {
        this.$message && this.$message.error('请先登录账号后再开通')
        return
      }

      this.stopPolling()
      this.qrUrl = ''
      this.orderId = ''
      this.successExpiry = ''
      this.payMessage = ''
      this.payState = 'claiming'
      this.payHint = '0 元功能无需支付，正在直接开通…'

      try {
        const response = await apiReq.permissionClaim({
          ...purchaseCatalog.buildClaimParams(item, user.id),
          source: this.purchaseSource || PURCHASE_SOURCE
        })
        const claimedUser = purchaseCatalog.resolvePaidUser(response)
        if (!claimedUser) {
          this.payState = 'failed'
          this.payMessage = (response && response.msg) || '开通失败，请稍后重试'
          return
        }
        // 写回全局登录态（统一入口，写入即广播），随后再按 id + auth_code 拉一次
        // /yljx/public/login 取权威最新用户信息，保证"开通后全局参数立即是最新的"
        userContext.writeStoredTianxingUser(claimedUser)
        const latestUser = (await refreshUser.refreshTianxingUser({ user: claimedUser })) || claimedUser
        this.payState = 'success'
        this.successExpiry = purchaseCatalog.resolveEntitlementStatus(item, latestUser).expiryDate
        await this.refreshItems()
        // freeClaim 标记：报警（点/面报警）的效果由画布直接读登录态判定，
        // 不经过星盘端 bootstrap 权限键，宿主据此走「就地重画」而不是整页刷新
        this.$emit('paid', { item, user: latestUser, freeClaim: true })
        this.$message && this.$message.success((response && response.msg) || '开通成功，功能已生效')
      } catch (error) {
        console.error('[FeaturePurchaseDialog] 0 元项自助开通失败:', error)
        this.payState = 'failed'
        this.payMessage = '开通失败，请稍后重试'
      }
    },
    resolvePayStyle () {
      const inWechat = typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
      return inWechat ? 'jsapi' : 'native'
    },
    async startPurchase (item) {
      if (!item) {
        return
      }
      const user = this.readCurrentUser()
      if (!user || user.id === undefined || user.id === null) {
        this.$message && this.$message.error('请先登录账号后再开通')
        return
      }

      this.stopPolling()
      this.payState = 'pending'
      this.payMessage = ''
      this.qrUrl = ''
      this.orderId = ''

      const style = this.resolvePayStyle()
      // 微信内 JSAPI 支付必须用该账号自己的 openid；未绑定微信先给绑定引导，不发支付请求
      if (style === 'jsapi' && !ensureWechatBound()) {
        this.payState = 'idle'
        return
      }
      // 扫码付款（native）同一口径：先确认账号已绑定微信；未绑定则引导绑定，绑定成功后就地续跑
      if (style !== 'jsapi' && !ensureScanPayBound(() => this.startPurchase(item))) {
        this.payState = 'idle'
        return
      }
      const params = {
        ...purchaseCatalog.buildPurchaseParams(item, user.id, style),
        source: this.purchaseSource || PURCHASE_SOURCE
      }

      try {
        const response = style === 'jsapi' ? await apiReq.h5pay(params) : await apiReq.getPay(params)
        if (!response || !response.data) {
          this.payState = 'failed'
          this.payMessage = (response && response.msg) || '下单失败，请稍后重试'
          return
        }
        this.orderId = response.data
        if (style === 'jsapi') {
          this.payHint = '请在微信中完成支付'
          if (!invokeWechatPay(response)) {
            this.stopPolling()
            this.payState = 'failed'
            this.payMessage = '支付未成功发起，请重新下单'
            return
          }
        } else {
          this.qrUrl = response.url || ''
          this.payHint = this.qrUrl ? '请用微信扫描二维码支付' : SCAN_PAY_TEXT.qrFailed
          if (!this.qrUrl) {
            this.payState = 'failed'
            this.payMessage = SCAN_PAY_TEXT.qrFailed
            return
          }
        }
        this.startPolling()
      } catch (error) {
        console.error('[FeaturePurchaseDialog] 发起支付失败:', error)
        this.payState = 'failed'
        this.payMessage = '支付发起失败，请稍后重试'
      }
    },
    startPolling () {
      this.pollCount = 0
      this.pollTimer = setInterval(this.pollOrder, 1000)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    /** 轮询查单：后端成功时返回最新 user，写回登录态并通知宿主刷新权限 */
    async pollOrder () {
      if (!this.orderId) {
        return
      }
      this.pollCount += 1
      if (this.pollCount > MAX_POLL_COUNT) {
        this.stopPolling()
        this.payState = 'failed'
        this.payMessage = '支付超时，请确认是否已付款后重新下单'
        return
      }

      try {
        const response = await apiReq.getLunxun({ id: this.orderId })
        const paidUser = purchaseCatalog.resolvePaidUser(response)
        if (!paidUser) {
          return
        }
        this.stopPolling()
        // 写回全局登录态（统一入口，写入即广播）；扫码付款（native）与 JSAPI 共用本回调，
        // 随后再按 id + auth_code 拉一次权威用户信息，确保开通的权益立刻进入全局参数，
        // 星盘页/顶栏等订阅方无需整页刷新即可生效
        userContext.writeStoredTianxingUser(paidUser)
        const latestUser = (await refreshUser.refreshTianxingUser({ user: paidUser })) || paidUser
        this.payState = 'success'
        this.qrUrl = ''
        const expireAt = this.selectedItem
          ? purchaseCatalog.resolveEntitlementStatus(this.selectedItem, latestUser).expiryDate
          : ''
        this.successExpiry = expireAt
        this.refreshItems()
        this.$emit('paid', { item: this.selectedItem, user: latestUser })
        this.$message && this.$message.success('开通成功，功能已生效')
      } catch (error) {
        console.error('[FeaturePurchaseDialog] 轮询订单状态失败:', error)
      }
    },
    handleClose () {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.feature-purchase-subtitle {
  margin: 0 0 14px;
  font-size: 13px;
  color: #909399;
}

.feature-purchase-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.feature-purchase-list {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.feature-card {
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.feature-card:hover {
  border-color: #ffb066;
  box-shadow: 0 4px 12px rgba(255, 133, 32, 0.18);
}

.feature-card.is-active {
  border: 2px solid #ff8520;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 133, 32, 0.25);
}

.feature-card.is-disabled {
  cursor: not-allowed;
  background: #fafafa;
  opacity: 0.75;
}

/* 0 元项（点报警 / 面报警）：点一下即开通一年，用一行绿色提示与扫码项区分；
   开通后提示转为灰色并随卡片一起置灰锁定 */
.feature-card-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #67c23a;
}

.feature-card-hint.is-locked {
  color: #909399;
}

.feature-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.feature-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.feature-badge {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.feature-badge.is-active {
  background: #f0f9eb;
  color: #67c23a;
}

.feature-badge.is-none {
  background: #f4f4f5;
  color: #909399;
}

.feature-badge.is-expired {
  background: #fdf6ec;
  color: #e6a23c;
}

.feature-card-desc {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  min-height: 32px;
}

.feature-card-foot {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.feature-card-price {
  font-size: 20px;
  font-weight: 600;
  color: #c79745;
}

.feature-card-expiry {
  font-size: 11px;
  color: #909399;
}

.feature-purchase-pay {
  width: 260px;
  padding: 16px;
  border-radius: 10px;
  background: #f5f7fa;
  text-align: center;
}

.feature-pay-title {
  font-size: 15px;
  font-weight: 500;
  color: #303133;
}

.feature-pay-amount {
  margin: 6px 0 12px;
  font-size: 24px;
  font-weight: 600;
  color: #c79745;
}

.feature-qr-wrap {
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.feature-qr {
  display: block;
  width: 200px;
  height: 200px;
  margin: 0 auto;
}

.feature-qr-tip {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #e6a23c;
  text-align: center;
}

.feature-qr-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 224px;
  border-radius: 8px;
  background: #fff;
  color: #909399;
  font-size: 12px;
}

.feature-order-no {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.feature-pay-status {
  margin: 8px 0 10px;
  font-size: 12px;
  color: #909399;
}

.feature-pay-status.is-pending {
  color: #409eff;
}

.feature-pay-status.is-success {
  color: #67c23a;
}

.feature-pay-status.is-failed {
  color: #f56c6c;
}

@media (max-width: 768px) {
  .feature-purchase-body {
    flex-direction: column;
  }

  .feature-purchase-list {
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }

  .feature-purchase-pay {
    width: 100%;
  }
}
</style>
